import { Fragment, useState} from 'react';
import { Card, Col } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import motimaticLogo from "src/assets/images/brand-logos/motimatic.png";
import PasswordChanged from './components/passwordChanged';
import { ChangePasswordService } from '@services/change-password/ChangePasswordService';
import PasswordForm from './components/PasswordForm';
/*
    Setting a new password from an emailed link (MPN-1778).

    A client reported that she "can enter the password and confirm it but the button doesn't
    work. The page doesn't take the changes." The button worked. The request went. The server
    answered 400 with a sentence saying exactly what was wrong -- the password was too short,
    or too common, or the link had already been used -- and this page logged it to a console
    she was never going to open, then did nothing.

    So the rule here is that a failed submit must always leave something on screen. There is
    no other feedback on this page: no toast host, no navigation, no spinner, and the form
    does not clear. Without a message, a rejected password is indistinguishable from a dead
    button, which is exactly how it was reported.
*/
export default function ChangePassword() {
    const [searchParams] = useSearchParams();
    const tokenFromUrl = searchParams.get('token') ?? '';
    const userIdEncoded = searchParams.get('user_id') ?? '';
    const [isChanged, setIsChanged] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const sendFormData = async (password: string) => {
        setError(null);
        setIsSaving(true);
        try {
            const service = new ChangePasswordService();
            await service.changePasswordExternal(tokenFromUrl, userIdEncoded, password);
            setIsChanged(true);
        } catch(error: any){
            // Whatever the server said, verbatim: it names the rule that was broken, and a
            // generic "something went wrong" would send her back to guessing.
            setError(error?.message || "That did not work. Please try again.");
        } finally {
            setIsSaving(false);
        }
    }

    if (isChanged) 
        return <PasswordChanged/>;
    
    return(
        <Fragment>
            <div className="container-lg">
                <div className="row justify-content-center authentication authentication-basic align-items-center h-100">
                    <Col xxl={4} xl={5} lg={5} md={6} sm={8} className="col-12">
                        <div className="my-5 d-flex justify-content-center">
                            <div className="my-5 d-flex justify-content-center no-background w-50 mx-auto">
                                <img src={motimaticLogo} alt="logo" className="img-fluid" />
                            </div>
                        </div>
                        <Card className="custom-card">
                            <div className="card-body p-5">
                                <p className="h5 fw-semibold mb-2 text-center">Change Password</p>

                                {/* A link that has expired or already been used fails the
                                    same way a weak password does, and the server says which.
                                    Shown above the fields so it is the first thing read. */}
                                {error && (
                                    <div className="alert alert-danger py-2 mb-3" role="alert">
                                        {error}
                                    </div>
                                )}

                                <PasswordForm sendFormData={sendFormData} isSaving={isSaving} />
                            </div>
                        </Card>
                    </Col>
                </div>
            </div>
        </Fragment>
    )
}